import React from 'react';
import { motion } from 'framer-motion';
import { Server, Network, Cpu, Globe, ShieldCheck, Headset } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { ServiceCard } from '../components/ServiceCard';
import { CTABanner } from '../components/CTABanner';
export function ServicesPage() {
  const services = [{
    icon: <Server className="w-8 h-8" data-id="element-483" />,
    title: 'ICT Infrastructure Design & Deployment',
    description: 'We provide end-to-end lifecycle management for your ICT infrastructure — from procurement and warehousing to deployment and ongoing maintenance. Our professional services team handles consulting, design, architecture, integration, and migration to ensure your infrastructure is built for performance and scale.',
    benefits: ['Procurement: Quotation, negotiation, and purchase management', 'Warehousing: Configuration, storage capacity, and worldwide delivery', 'Deployment: Vendor-certified experts, setup, testing, and training', 'Maintenance: Contract renewals, consolidation, multi-vendor support, and end-of-life equipment disposal', 'Professional Services: Consulting, design, architecture, integration, and migration']
  }, {
    icon: <Network className="w-8 h-8" data-id="element-484" />,
    title: 'Systems Integration',
    description: 'Our team of IT consultants and Engineers are experienced and capable of deploying a full spectrum of technology while reducing risk. We have the facility to build, power up, configure, upgrade OS or firmware, label and prepare your equipment prior to shipping to the site for onsite installation. We can manage the RMA process and prevent critical installation timelines from being thrown off track in the field. Our integration process and strategy has proven to be beneficial for small installations and large multi-site global deployments.',
    benefits: ['Solution-based approach tailored to your business needs', 'Maximum return on investment through strategic planning', 'Best-of-breed processes for optimal results', 'On-time, high-quality deliverables']
  }, {
    icon: <ShieldCheck className="w-8 h-8" data-id="element-485" />,
    title: 'Managed IT Services',
    description: 'Focus on your core business while we handle your IT operations. We provide proactive monitoring, maintenance, and support to ensure your systems are always running optimally and securely.',
    benefits: ['24/7 proactive system monitoring', 'Helpdesk and technical support', 'Patch management and security updates', 'Disaster recovery and backup management']
  }, {
    icon: <Cpu className="w-8 h-8" data-id="element-486" />,
    title: 'Business Process Management',
    description: "We deliver a comprehensive 5-step BPM cycle — from defining business objectives and strategy through modeling, automation, monitoring, and optimization. Our approach aligns BPM strategy with your company's strategic goals and change management initiatives to drive measurable results.",
    benefits: ['Business Objectives: BPM strategy definition, Center of Excellence conformation, and change management execution', 'Modeling: Process and requirement gathering, BPMN AsIs/ToBe modeling, and BPMN notation training', 'Automation: Implementation of automation projects, process factory, SOA integration, and quality assurance', 'Monitoring: Assessment of indicators, alerts, improvement proposals, and multi-level helpdesk support', 'Optimization: Six Sigma consulting methodology, process re-design, and identification of new automation needs']
  }, {
    icon: <Globe className="w-8 h-8" data-id="element-487" />,
    title: 'Digital Inclusion & Advocacy',
    description: 'Invigo promotes digital inclusion through its analysis of the economic and social impacts of future and near-future ICT trends. From cloud computing and the power of big data to the latest Internet applications that simplify everyday tasks, cutting-edge technologies on the horizon offer opportunities to accelerate economic growth, increase social inclusion, and provide new tools in the fight against poverty.',
    benefits: ['Policy analysis, capacity building, and partnership engagement for inclusive development', 'Tools and products enabling government ministries and civil society to mainstream disaster risk reduction', 'Analysis of regional trends related to smart cities, cybersecurity, and e-resilience', 'Communication technologies for inclusive and sustainable development']
  }, {
    icon: <Headset className="w-8 h-8" data-id="element-488" />,
    title: 'ICT Products Delivery & Marketing Outsourcing',
    description: 'We provide end-to-end delivery of premium enterprise hardware, software licenses, and networking equipment. Our marketing outsourcing services help technology manufacturers reach the African market effectively through our extensive partner network.',
    benefits: ['End-to-end product delivery and logistics', 'Marketing outsourcing for technology manufacturers', 'Competitive pricing for enterprise procurement', 'Hardware warranty and support services']
  }];
  return <main className="w-full pt-20" data-id="element-489">
      {/* Page Header */}
      <section className="bg-navy-900 py-20 relative overflow-hidden" data-id="element-490">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-491" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-tech-blue/10 blur-[100px] rounded-full" data-id="element-492" />

        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-493">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-494">
            Our <span className="text-tech-green" data-id="element-495">Services</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-slate-300 max-w-2xl" data-id="element-496">
            Comprehensive, end-to-end technology solutions designed to drive
            efficiency, security, and growth for your organization.
          </motion.p>
        </div>
      </section>

      {/* Services Detailed List */}
      <section className="py-24 bg-slate-50" data-id="element-497">
        <div className="max-w-7xl mx-auto px-4" data-id="element-498">
          <div className="grid lg:grid-cols-2 gap-8" data-id="element-499">
            {services.map((service, idx) => <div key={idx} className="bg-navy-900 rounded-3xl overflow-hidden shadow-xl" data-id="element-500">
                <ServiceCard icon={service.icon} title={service.title} description={service.description} benefits={service.benefits} isExpandable={true} delay={idx * 0.1} data-id="element-501" />
              </div>)}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-white" data-id="element-502">
        <div className="max-w-7xl mx-auto px-4" data-id="element-503">
          <SectionHeading title="Our Approach" subtitle="How we deliver excellence in every project." centered data-id="element-504" />

          <div className="grid md:grid-cols-4 gap-8 mt-16 relative" data-id="element-505">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 z-0" data-id="element-506" />

            {[{
            step: '01',
            title: 'Discover',
            desc: 'Deep dive into your business needs and current infrastructure.'
          }, {
            step: '02',
            title: 'Design',
            desc: 'Architecting a scalable, secure, and cost-effective solution.'
          }, {
            step: '03',
            title: 'Deploy',
            desc: 'Seamless implementation with minimal disruption to operations.'
          }, {
            step: '04',
            title: 'Manage',
            desc: 'Ongoing support, monitoring, and optimization.'
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="relative z-10 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm text-center" data-id="element-507">
                <div className="w-12 h-12 mx-auto rounded-full bg-tech-blue text-white font-bold flex items-center justify-center mb-4 shadow-lg shadow-tech-blue/30" data-id="element-508">
                  {item.step}
                </div>
                <h4 className="text-lg font-bold text-navy-900 mb-2" data-id="element-509">
                  {item.title}
                </h4>
                <p className="text-sm text-slate-600" data-id="element-510">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <CTABanner data-id="element-511" />
    </main>;
}